
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Mail, Download, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { renderHtmlContent, stripHtmlTags } from "@/utils/htmlUtils";
import { GET_PUBLIC_LETTER, isLegacyLetterUuid, publicLetterFromRpc } from "@/utils/letterShare";
import { generateLetterPDF } from "@/utils/pdfGenerator";

export default function SharedLetter() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const legacyLink = !!id && isLegacyLetterUuid(id);

  const { data: view, isLoading } = useQuery({
    queryKey: ["shared-letter", id],
    queryFn: async () => {
      if (!id) return { status: "unavailable" as const };

      const { data, error } = await supabase.rpc(GET_PUBLIC_LETTER, { p_token: id });
      return publicLetterFromRpc({ data, error });
    },
    enabled: !!id && !legacyLink,
  });

  const letter = view?.status === "ready" ? view.letter : null;

  const handleDownload = async () => {
    if (!letter) return;
    
    try {
      const cleanContent = stripHtmlTags(letter.content);
      const deliveryDate = new Date(letter.delivery_date + "T00:00:00").toLocaleDateString("pt-BR");

      await generateLetterPDF(cleanContent, deliveryDate);

      toast({
        title: "Download iniciado",
        description: "Sua carta será baixada em formato PDF.",
      });
    } catch {
      toast({
        variant: "destructive",
        title: "Erro no download",
        description: "Não foi possível baixar a carta.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Carregando carta...</p>
        </div>
      </div>
    );
  }

  if (legacyLink || !letter) {
    const invalid = legacyLink;
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Mail className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {invalid ? "Este link não é mais válido" : "Carta indisponível"}
          </h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {invalid
              ? "O endereço antigo não abre a carta. Peça um link novo a quem a escreveu."
              : "Não há conteúdo para este link. Ele pode ter sido revogado ou ainda não existe."}
          </p>
          <Link to="/">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Voltar ao início
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Render HTML content properly
  const htmlContent = renderHtmlContent(letter.content || "");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Mail className="h-8 w-8 text-green-600" />
            <h1 className="text-3xl font-bold text-gray-900">Carta do Futuro</h1>
          </div>
          <p className="text-gray-600">
            Entregue em {format(new Date(letter.delivery_date + "T00:00:00"), "dd 'de' MMMM, yyyy", { locale: ptBR })}
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Letter Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-green-600" />
                  Sua Carta
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <div 
                    className="text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Letter Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-green-600" />
                  Informações
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <Badge className="bg-green-100 text-green-800">
                      Entregue
                    </Badge>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Entrega</label>
                  <p className="mt-1 text-gray-900">
                    {format(new Date(letter.delivery_date + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR })}
                  </p>
                </div>

                <Button onClick={handleDownload} className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Gostou? Crie suas próprias cartas do futuro!
                  </p>
                  <Link to="/">
                    <Button variant="outline" className="w-full">
                      Criar minha carta
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
